import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Edit Punch Dialog Component
 *
 * Allows administrators to edit punch timestamps with required reason.
 * Maintains audit trail for compliance.
 */
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { X, Clock, AlertTriangle } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useAuthStore } from '../../stores/auth.store';
import { config } from '../../lib/config';
const TIME_API_URL = config.api.time;
function getActionLabel(action) {
    switch (action) {
        case 'CLOCK_IN': return 'Clock In';
        case 'CLOCK_OUT': return 'Clock Out';
        case 'BREAK_START': return 'Break Start';
        case 'BREAK_END': return 'Break End';
        default: return action;
    }
}
function getActionColor(action) {
    switch (action) {
        case 'CLOCK_IN': return 'bg-green-100 text-green-800';
        case 'CLOCK_OUT': return 'bg-red-100 text-red-800';
        case 'BREAK_START': return 'bg-yellow-100 text-yellow-800';
        case 'BREAK_END': return 'bg-blue-100 text-blue-800';
        default: return 'bg-gray-100 text-gray-800';
    }
}
async function updatePunch(punchId, data) {
    const tokens = useAuthStore.getState().tokens;
    const response = await fetch(`${TIME_API_URL}/punches/${punchId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens?.idToken || ''}`,
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update punch: ${response.statusText}`);
    }
}
export function EditPunchDialog({ punch, employeeName, onClose, onSuccess }) {
    const queryClient = useQueryClient();
    // Parse the existing timestamp to local date and time
    const existingDate = parseISO(punch.timestamp);
    const [date, setDate] = useState(format(existingDate, 'yyyy-MM-dd'));
    const [time, setTime] = useState(format(existingDate, 'HH:mm'));
    const [reason, setReason] = useState('');
    const mutation = useMutation({
        mutationFn: (data) => updatePunch(punch.punchId, data),
        onSuccess: () => {
            toast.success('Punch Updated', {
                description: `${getActionLabel(punch.action)} time has been updated.`,
            });
            // Invalidate punch history queries to refresh the data
            queryClient.invalidateQueries({ queryKey: ['punchHistory'] });
            onSuccess?.();
            onClose();
        },
        onError: (error) => {
            toast.error('Failed to Update Punch', {
                description: error.message,
            });
        },
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!reason.trim() || reason.trim().length < 3) {
            toast.error('Reason Required', {
                description: 'Please provide a reason for this change (minimum 3 characters).',
            });
            return;
        }
        // Combine date and time into ISO timestamp
        const newTimestamp = new Date(`${date}T${time}:00`).toISOString();
        mutation.mutate({
            timestamp: newTimestamp,
            reason: reason.trim(),
        });
    };
    const hasChanges = () => {
        const newTimestamp = new Date(`${date}T${time}:00`).toISOString();
        return newTimestamp !== punch.timestamp;
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", children: _jsxs("div", { className: "relative w-full max-w-md rounded-lg bg-white shadow-xl", children: [_jsxs("div", { className: "flex items-center justify-between border-b px-6 py-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "rounded-full bg-blue-100 p-2", children: _jsx(Clock, { className: "h-5 w-5 text-blue-600" }) }), _jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "Edit Punch" }), _jsx("p", { className: "text-sm text-gray-500", children: employeeName })] })] }), _jsx("button", { onClick: onClose, disabled: mutation.isPending, className: "rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50", children: _jsx(X, { className: "h-5 w-5" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "p-6 space-y-4", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm text-gray-600", children: "Type:" }), _jsx(Badge, { className: getActionColor(punch.action), children: getActionLabel(punch.action) })] }), _jsxs("div", { className: "rounded-lg bg-gray-50 p-3", children: [_jsx("p", { className: "text-xs text-gray-500 mb-1", children: "Current Time" }), _jsx("p", { className: "font-mono text-gray-900", children: format(existingDate, 'MMM d, yyyy h:mm:ss a') })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "date", children: "New Date" }), _jsx(Input, { id: "date", type: "date", value: date, onChange: (e) => setDate(e.target.value), max: format(new Date(), 'yyyy-MM-dd'), disabled: mutation.isPending, required: true })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "time", children: "New Time" }), _jsx(Input, { id: "time", type: "time", value: time, onChange: (e) => setTime(e.target.value), disabled: mutation.isPending, required: true })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs(Label, { htmlFor: "reason", children: ["Reason for Change ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(Textarea, { id: "reason", value: reason, onChange: (e) => setReason(e.target.value), placeholder: "e.g., Employee forgot to clock in, correcting error...", disabled: mutation.isPending, rows: 3, required: true, minLength: 3 }), _jsx("p", { className: "text-xs text-gray-500", children: "This will be recorded in the audit trail" })] }), _jsxs("div", { className: "flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3", children: [_jsx(AlertTriangle, { className: "h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-xs text-yellow-800", children: "This is an administrative edit. The change will be recorded with your user ID for audit purposes. For employee-initiated changes, use the correction request workflow." })] }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onClose, disabled: mutation.isPending, className: "flex-1", children: "Cancel" }), _jsx(Button, { type: "submit", disabled: mutation.isPending || !hasChanges() || !reason.trim(), className: "flex-1 bg-blue-600 hover:bg-blue-700", children: mutation.isPending ? 'Saving...' : 'Save Changes' })] })] })] }) }));
}
